/**
 * SuggestProductForm — 5-field prominent form for users to suggest a
 * missing product (perishable, foreign, niche items).
 *
 * Route: /food-pulse/suggest-product
 *
 * Submits to POST /api/pending-products with needs_research=true and
 * scan_source="suggest-form". Admin picks up in the existing queue at
 * /admin/foodpulse (Products tab).
 */
import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Sparkles, Camera, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const CATEGORIES = [
  { value: "perishable",     label: "Perishable (fresh produce, dairy, meat)" },
  { value: "non-perishable", label: "Non-perishable (canned, dry, packaged)" },
  { value: "foreign",        label: "Foreign / imported item" },
  { value: "beverage",       label: "Beverage" },
  { value: "non-food",       label: "Non-food grocery (cleaning, toiletries, pet)" },
  { value: "other",          label: "Other" },
];

export default function SuggestProductForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [urlParams] = useSearchParams();
  const prefilledName = urlParams.get("name") || "";
  const missingField  = urlParams.get("missing") || "";   // article | nutrition | recipes | tips_links
  const missingLabel  = {
    article: "the article / basic info",
    nutrition: "nutrition data",
    recipes: "recipes",
    tips_links: "tips & links",
  }[missingField];
  const [form, setForm] = useState({
    name: prefilledName,
    product_category: "perishable",
    country_of_origin: "",
    notes: missingLabel ? `Please add ${missingLabel} for this product.` : "",
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [done, setDone] = useState(null);   // holds the submission id when done

  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(`/lists/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((p) => [...p, r.data.url]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try a different photo.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await http.post(`/pending-products`, {
        name:              form.name.trim(),
        product_category:  form.product_category,
        country_of_origin: form.country_of_origin || null,
        notes:             form.notes || null,
        image_urls:        images,
        scan_source:       "suggest-form",
        needs_research:    true,
      });
      setDone(r.data.id);
    } catch (err) {
      if (err?.response?.status === 401) {
        nav("/login?next=" + encodeURIComponent(window.location.pathname));
      } else {
        setError(err?.response?.data?.detail || "Submission failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    // Outer FoodPulseAuthGate typically catches this before we render; kept
    // as a safety net for direct hits on the route.
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Sparkles className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-3">Suggest a product</h1>
          <p className="opacity-80 mb-6">Sign in to suggest a missing product. We'll research the details and add it for everyone.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="suggest-login-cta">Sign in</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }} data-testid="suggest-product-thanks">
        <div className="max-w-md text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#4CAF50" }} />
          <h1 className="font-display text-3xl mb-3">Thanks — we're on it!</h1>
          <p className="opacity-80 mb-6">Your suggestion is in the queue. Our team will research the details (nutrition, cooking tips, recipes, origin story) and publish it to the catalog. You'll be able to find it in the Products Directory once approved.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setDone(null); setError(""); setForm({ name: "", product_category: "perishable", country_of_origin: "", notes: "" }); setImages([]); }}
                    className="rounded-full px-5 py-2.5 border font-semibold" style={{ borderColor: GREEN, color: GREEN }}
                    data-testid="suggest-another">Suggest another</button>
            <Link to="/food-pulse/food" className="rounded-full px-5 py-2.5 font-semibold text-white" style={{ background: GREEN }} data-testid="suggest-back-to-directory">Back to directory</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="suggest-product-page">
      <SeoHead section="food-pulse-suggest-product" title="Suggest a product — Food Pulse" description="Can't find a product in our catalog? Tell us what it is and we'll research the details." />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <Link to="/food-pulse/food" className="inline-flex items-center gap-1 text-xs mb-3 opacity-70 hover:opacity-100">
          <ArrowLeft className="w-3 h-3" /> Back to directory
        </Link>
        <h1 className="font-display text-4xl mb-2" style={{ color: GREEN }}>Can't find a product?</h1>
        <p className="text-sm opacity-70 mb-8">Tell us the name and (optionally) attach a photo. We'll research the nutrition, cooking tips, storage and recipes, and add it to the catalog for everyone.</p>

        <form onSubmit={submit} className="space-y-5">
          {/* 1. Name */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: GREEN }}>Product name *</label>
            <input value={form.name} onChange={change("name")}
                   placeholder="e.g. Sapodilla, Longan, Manchego cheese"
                   required maxLength={200}
                   className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#E9D8B4", background: "#fff" }}
                   data-testid="suggest-name" />
          </div>

          {/* 2. Category */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: GREEN }}>Category *</label>
            <select value={form.product_category} onChange={change("product_category")}
                    className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#E9D8B4", background: "#fff" }}
                    data-testid="suggest-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* 3. Country of origin */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: GREEN }}>Country of origin</label>
            <input value={form.country_of_origin} onChange={change("country_of_origin")}
                   placeholder="e.g. Thailand, Mexico, Italy — optional"
                   maxLength={120}
                   className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#E9D8B4", background: "#fff" }}
                   data-testid="suggest-country" />
          </div>

          {/* 4. Photo */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: GREEN }}>Photo</label>
            <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 cursor-pointer hover:bg-white/50 transition"
                   style={{ borderColor: "#B8D3A1" }}>
              <input type="file" accept="image/*" capture="environment" onChange={onPickPhoto} className="hidden" data-testid="suggest-photo-input" />
              {uploading ? <Loader2 className="w-5 h-5 animate-spin opacity-60" /> : <Camera className="w-5 h-5 opacity-60" />}
              <span className="text-sm opacity-70">{uploading ? "Uploading…" : "Snap or upload a photo"}</span>
            </label>
            {images.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap" data-testid="suggest-photo-thumbs">
                {images.map((url, i) => (
                  <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover border" style={{ borderColor: "#E9D8B4" }} />
                ))}
              </div>
            )}
          </div>

          {/* 5. Notes */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: GREEN }}>Anything else?</label>
            <textarea value={form.notes} onChange={change("notes")}
                      placeholder="Where did you see it? Season, brand, taste, common uses…"
                      rows={4}
                      className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#E9D8B4", background: "#fff" }}
                      data-testid="suggest-notes" />
          </div>

          <button type="submit" disabled={submitting || !form.name.trim()}
                  className="w-full rounded-full px-6 py-3.5 font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  style={{ background: GREEN }}
                  data-testid="suggest-submit">
            {submitting ? "Submitting…" : "Submit — we'll research and add it"}
          </button>
          {error && (
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="suggest-error">
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
